import React, { useState } from 'react';
import TextType from '../TextType/TextType';
import SpriteAnimatedLogo from '../ui/SpriteAnimatedLogo.js';
import { SPRITES } from '../ui/SpriteAnimatedLogo.js';
import { DASHBOARD_URL } from '../config/const.js';
import './Hero.css';

const Hero = () => {
  const [expandedFile, setExpandedFile] = useState(null);
  const words = ['Organized.', 'Linked.', 'Evolved.', 'Never forget.'];

  // Simple markdown renderer
  const renderMarkdown = (markdown) => {
    let html = markdown;

    // Code blocks first (to avoid interference with other replacements)
    html = html.replace(/```[\s\S]*?```/gim, (match) => {
      const content = match.replace(/```/g, '').trim();
      return `<pre class="code-block">${content}</pre>`;
    });

    // Split by lines for line-based processing
    const lines = html.split('\n');
    const processedLines = [];
    let inList = false;
    let listItems = [];

    lines.forEach(line => {
      const trimmedLine = line.trim();
      
      // Skip if line is inside a code block
      if (line.includes('<pre class="code-block">') || line.includes('</pre>')) {
        processedLines.push(line);
        return;
      }

      // Headers
      if (trimmedLine.startsWith('### ')) {
        if (inList) { processedLines.push('<ul>' + listItems.join('') + '</ul>'); inList = false; listItems = []; }
        processedLines.push('<h3>' + trimmedLine.substring(4) + '</h3>');
      } else if (trimmedLine.startsWith('## ')) {
        if (inList) { processedLines.push('<ul>' + listItems.join('') + '</ul>'); inList = false; listItems = []; }
        processedLines.push('<h2>' + trimmedLine.substring(3) + '</h2>');
      } else if (trimmedLine.startsWith('# ')) {
        if (inList) { processedLines.push('<ul>' + listItems.join('') + '</ul>'); inList = false; listItems = []; }
        processedLines.push('<h1>' + trimmedLine.substring(2) + '</h1>');
      }
      // Lists  
      else if (trimmedLine.startsWith('- ')) {
        if (!inList) inList = true;
        listItems.push('<li>' + trimmedLine.substring(2) + '</li>');
      } else if (/^\d+\.\s/.test(trimmedLine)) {
        if (!inList) inList = true;
        listItems.push('<li>' + trimmedLine.replace(/^\d+\.\s/, '') + '</li>');
      }
      // Regular lines
      else {
        if (inList) {
          processedLines.push('<ul>' + listItems.join('') + '</ul>');
          inList = false;
          listItems = [];
        }
        if (trimmedLine) {
          processedLines.push('<p>' + trimmedLine + '</p>');
        }
      }
    });

    // Handle remaining list items
    if (inList) {
      processedLines.push('<ul>' + listItems.join('') + '</ul>');
    }

    html = processedLines.join('');

    // Apply inline formatting
    html = html
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      // Italic (but not inside asterisks used for bold)
      .replace(/(?<!\*)\*([^*]+)\*(?!\*)/gim, '<em>$1</em>')
      // Inline code
      .replace(/`([^`]+)`/gim, '<code class="inline-code">$1</code>');

    return html;
  };

  const markdownFiles = [
    {
      id: 'profile',
      name: 'profile.md',
      icon: '👤',
      color: '#7DCBF7',
      content: `# User Profile Analysis

## John Smith - Senior Frontend Developer

### Basic Information
- **Company**: TechCorp Inc.
- **Role**: Lead Frontend Developer (Level: Senior III)
- **Location**: San Francisco, CA
- **Team**: Manages 3 junior developers
- **Experience**: 3.2 years at company

### Contact & Preferences
- **Email**: john.smith@techcorp.com
- **Slack**: @johnsmith
- **Meeting Time**: Early morning (9-11 AM PST)
- **Communication**: Prefers email for formal, Slack for quick questions

### Technical Skills
**Expert Level**:
- JavaScript, TypeScript, React.js, Next.js
- Webpack, Vite, Jest, Cypress

**Proficient Level**:
- Node.js, PostgreSQL, AWS, Docker

**Learning Areas**:
- WebAssembly, Micro-frontends, Performance Optimization

### Current Projects
1. **User Authentication Redesign** (70% complete)
   - React, TypeScript, OAuth 2.0
   - Due: Feb 2024

2. **Performance Optimization Initiative**
   - Focus: Core Web Vitals improvement
   - Expected: 40% faster load times

### Work Style
**Strengths**:
- Detail-oriented and proactive
- Excellent mentor and communicator
- Strong advocate for code quality

**Preferences**:
- Peak productivity: 9 AM - 1 PM PST
- Thorough code reviews with educational comments
- Data-driven discussions

### Performance Metrics
- **Code Review Quality**: 9.2/10
- **On-time Delivery**: 95%
- **Bug Rate**: 0.3/1000 lines (team avg: 1.2)
- **Team Satisfaction**: 4.8/5

### Career Goals
- **Short-term**: Lead frontend architecture, expand team
- **Medium-term**: Transition to Engineering Manager
- **Long-term**: Head of Frontend Engineering`
    },
    {
      id: 'events',
      name: 'events.md',
      icon: '📅',
      color: '#10B981',
      content: `# Event Timeline

## Recent Activity (Last 7 Days)

### 2024-01-20 - Critical Support Incident
**Issue**: Authentication timeout affecting 15% of users  
**Severity**: P1 (Critical)  
**Resolution**: Emergency patch deployed in 1h 15min  
**Status**: ✅ Resolved

### 2024-01-20 - Q1 Planning Session  
**Type**: Strategic Planning Meeting  
**Organizer**: Sarah Chen  
**Key Decisions**:  
- ✅ Feature freeze on Feb 15, 2024  
- ✅ Client demo scheduled for Feb 1, 2024  
- ✅ Performance optimization sprint (Feb 19-Mar 5)

### 2024-01-19 - Production Deployment
**Version**: v2.3.1  
**Changes**: 7 bug fixes, performance improvements, security updates  
**Status**: ✅ Successful deployment  

### 2024-01-19 - Code Review Session
**Reviewer**: John Smith  
**PRs Reviewed**: 3 pull requests  
**Total Changes**: +847 -234 lines of code

### 2024-01-19 - Client Strategy Call  
**Client**: ABC Corporation  
**Outcome**: ✅ Contract renewal approved for 24 months  
**Growth**: 20% increase in license count (500 → 600 users)

## Upcoming Events

### 2024-01-22 (Monday)
- **09:00**: Sprint 24 Daily Standup
- **10:30**: New Employee Onboarding (2 new hires)
- **14:00**: Client demo preparation meeting

### 2024-01-23 (Tuesday)
- **09:00**: All-hands company meeting
- **11:00**: Performance optimization planning session
- **13:00**: Lunch & Learn: "Advanced React Patterns"

## Event Statistics (Last 30 Days)
- **Total Events**: 47
- **Categories**: Development (38%), Meetings (26%), Support (19%)
- **Critical Events**: 3 (All resolved)
- **Average Resolution Time**: 2.4 hours`
    },
    {
      id: 'insights',
      name: 'insights.md',
      icon: '📊',
      color: '#F59E0B',
      content: `# Memory Insights Dashboard

## Executive Summary
- **Total Memories**: 12,847 active entries
- **Query Volume**: 3,247 searches/day (↑15% from last month)
- **System Health**: 99.7% uptime, 0.3s avg response time
- **User Satisfaction**: 4.6/5

## Usage Analytics

### Memory Categories
1. **Customer Interactions** - 5,781 entries (45%)
2. **Team Communications** - 3,597 entries (28%)
3. **Project Documentation** - 2,312 entries (18%)
4. **Technical Notes** - 1,157 entries (9%)

### Peak Usage Hours
- **Primary Peak**: 10:00 AM - 12:00 PM (847 avg queries/hour)
- **Secondary Peak**: 2:00 PM - 4:00 PM (623 avg queries/hour)

### Performance Metrics
- **Average Response Time**: 0.34s (Target: <0.5s)
- **Success Rate**: 94.2% (↑2.1% from last month)
- **Cache Hit Rate**: 78.3%

### Most Searched Topics
1. Customer preferences - 1,234 searches
2. Project deadlines - 967 searches
3. Team member contact info - 789 searches
4. Meeting summaries - 656 searches
5. Bug resolution status - 543 searches

### Top Users by Query Volume
1. **John Smith** - 247 queries/week (React, code reviews)
2. **Sarah Chen** - 198 queries/week (Team performance, projects)
3. **Mike Chen** - 187 queries/week (Timelines, resources)

## AI Insights

### Pattern Recognition
- Code reviews spike after Monday standups (78% confidence)
- Team meetings most productive on Tuesdays (92% confidence)
- Performance issues correlate with code reviews (87% confidence)

### Predictive Analytics
- Authentication project: 87% likely to complete on time
- Performance optimization: 94% likely to finish early
- John Smith approaching capacity (85% utilization)

## Business Impact
- **Time Saved**: 14.7 hours/week per team member
- **ROI**: 347% return on investment
- **Faster Decision Making**: 45% improvement
- **Knowledge Transfer**: 89% reduction in repetitive questions`
    },
    {
      id: 'connections',
      name: 'connections.md',
      icon: '🔗',
      color: '#8B5CF6',
      content: `# Memory Connections Network

## Network Overview
- **Total Connections**: 47,892 active links
- **Network Density**: 0.73 (highly interconnected)
- **Strong Connections** (weight >0.8): 12,456 (26%)
- **Emerging Connections** (last 7 days): 347 new links

## Key People Connections

### John Smith - Frontend Lead
- **Total Connections**: 2,847 memories (22% of network)
- **Strongest Topics**: React (341 connections), TypeScript (298), Code Reviews (267)
- **Key Collaborators**: Sarah Chen (156 memories), Rachel Green (134), Lisa Wong (98)
- **Influence Score**: 8.7/10

### Sarah Chen - Engineering Manager
- **Total Connections**: 3,234 memories (25% of network)
- **Strongest Topics**: Team Management (445), Project Planning (387), Performance Reviews (298)
- **Leadership Style**: Data-driven (89% decisions backed by metrics)

### Mike Chen - Project Manager
- **Total Connections**: 2,156 memories (17% of network)
- **Strongest Topics**: Timeline Management (398), Resource Allocation (356), Risk Assessment (289)
- **Meeting Frequency**: 3.2 meetings/day average

## Topic Clusters

### Authentication System (Central Hub)
\`\`\`
Authentication System (847 connections)
├── OAuth Implementation (234 memories)
├── Session Management (198 memories)
├── Password Security (156 memories)
└── User Permissions (123 memories)
\`\`\`

### React Development Ecosystem  
\`\`\`
React Component Library (1,234 connections)
├── Design System Integration (345 memories)
├── Performance Optimization (298 memories)
├── Testing Infrastructure (267 memories)
└── TypeScript Integration (189 memories)
\`\`\`

## Auto-Generated Insights

### Recent Pattern Discovery (Last 30 Days)
1. **Performance Issues ↔ Code Reviews** (87% confidence)
2. **Customer Feedback ↔ Feature Development** (92% confidence)
3. **Bug Reports ↔ Testing Procedures** (79% confidence)

### Cross-Team Collaboration
- **Engineering ↔ Product**: 2,456 shared memories (8.4/10 score)
- **Customer Success ↔ Engineering**: 92% P1 issues resolved within SLA
- **Design ↔ Development**: 91% design specifications met

## Network Health
- **High Quality** connections (0.8-1.0): 67%
- **Knowledge Distribution Score**: 7.2/10
- **Single Points of Failure**: 3 identified (mitigation planned)`
    }
  ];

  const handleFileClick = (fileId) => {
    setExpandedFile(expandedFile === fileId ? null : fileId);
  };

  return (
    <section className="hero" id="agent-memory">
      <div className="hero-content">
        <h1>Agent Memory for AI.<br /><TextType text={words} pauseDuration={3000} textColors={['#7DCBF7']} /></h1>
        <p>MemU is an agent memory layer for LLM applications,<br />
          enabling autonomous, intelligent memory management for AI agents<br />
          <div className="hero-slogan-container">
            <div style={{ display: 'flex' }}><p style={{ fontWeight: 'bold' }}>H</p><p>igher accuracy</p></div>
            <div style={{ display: 'flex' }}><p style={{ fontWeight: 'bold' }}>F</p><p>aster retrieval</p></div>
            <div style={{ display: 'flex' }}><p style={{ fontWeight: 'bold' }}>L</p><p>ower cost</p></div>
          </div>
        </p>
        <a href={DASHBOARD_URL} className="btn btn-primary btn-lg" target="_blank" rel="noopener noreferrer">
          Get started with agent memory
        </a>
      </div>
      <div className="workflow-container">
        {/* Left side - Chat interface */}
        <div className="chat-section">
          <div className="chat-header">
            <div className="chat-title">
              <div className="chat-avatar">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#7DCBF7"></circle>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="white" strokeWidth="2" strokeLinecap="round"></path>
                </svg>
              </div>
              <span>MemU Assistant</span>
            </div>
            <div className="chat-status">
              <span className="status-dot"></span>
              <span>Online</span>
            </div>
          </div>
          
          <div className="chat-messages">
            <div className="message user-message">
              <div className="message-content">
                How can I store memories about my customers and retrieve them when needed?
              </div>
              <div className="message-time">10:30 AM</div>
            </div>
            
            <div className="message ai-message">
              <div className="message-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#7DCBF7"></circle>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="white" strokeWidth="2" strokeLinecap="round"></path>
                </svg>
              </div>
              <div className="message-content">
                MemU makes this easy! You can store structured customer information and retrieve it contextually. Let me show you how to:
                <br/><br/>
                1. Create customer memories<br/>
                2. Add relationship data<br/>
                3. Query based on context
              </div>
              <div className="message-time">10:31 AM</div>
            </div>
            
            <div className="message user-message">
              <div className="message-content">
                That sounds perfect! Can you show me some examples?
              </div>
              <div className="message-time">10:32 AM</div>
            </div>
            
            <div className="message ai-message typing-message">
              <div className="message-avatar">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" fill="#7DCBF7"></circle>
                  <path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01" stroke="white" strokeWidth="2" strokeLinecap="round"></path>
                </svg>
              </div>
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
          
        </div>
        
        {/* Middle - Flowing arrow with cat */}
        <div className="arrow-section">
          <div className="flowing-arrow-container">
            <svg className="flowing-arrow" viewBox="0 0 200 60" fill="none">
              <defs>
                <linearGradient id="flowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="transparent" />
                  <stop offset="30%" stopColor="var(--primary)" stopOpacity="0.6" />
                  <stop offset="70%" stopColor="var(--primary)" />
                  <stop offset="100%" stopColor="var(--primary-dark)" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge> 
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              <path 
                d="M10 30 Q50 15 100 30 Q150 45 180 30" 
                stroke="url(#flowGradient)" 
                strokeWidth="3" 
                fill="none"
                filter="url(#glow)"
                className="arrow-path"
              />
              <path 
                d="M170 25 L180 30 L170 35 L175 30 Z" 
                fill="var(--primary-dark)"
                className="arrow-head"
              />
            </svg>
            
            {/* Cat static on the arrow */}
            <div className="cat-on-arrow">
              <SpriteAnimatedLogo
                width={80}
                height={80}
                frameRate={8}
                autoPlay={true}
                filename={SPRITES.ANIM_WALK} 
              />
            </div>
          </div>
        </div>
        
        {/* Right side - Markdown file thumbnails */}
        <div className="markdown-thumbnails">
          <div className="thumbnails-header">
            <h3>Documentation</h3>
            <div className="file-count">{markdownFiles.length} files</div>
          </div>
          
          <div className="thumbnails-grid">
            {markdownFiles.map((file, index) => (
              <div 
                key={file.id} 
                className={`file-thumbnail ${expandedFile === file.id ? 'expanded' : ''}`}
                onClick={() => handleFileClick(file.id)}
                style={{ '--file-color': file.color }}
              >
                <div className="thumbnail-header">
                  <div className="file-icon-large">{file.icon}</div>
                  <div className="file-info">
                    <div className="file-name">{file.name}</div>
                    <div className="file-type">Markdown</div>
                  </div>
                  <div className="expand-icon">
                    {expandedFile === file.id ? '−' : '+'}
                  </div>
                </div>
                
                {expandedFile === file.id && (
                  <div className="expanded-content">
                    <div 
                      className="markdown-content"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(file.content) }}
                    />
                  </div>
                )}
                
                {expandedFile !== file.id && (
                  <div className="thumbnail-preview">
                    <div className="preview-lines">
                      <div className="preview-line"></div>
                      <div className="preview-line short"></div>
                      <div className="preview-line"></div>
                      <div className="preview-line medium"></div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;